/**
 * Tiered search-result ranker for TMDB results.
 *
 * TMDB's search uses n-gram from the START of titles — no fuzzy matching,
 * no typo tolerance, and multi-word queries match the verbatim substring.
 * That's why the dropdown felt bad: "intersteler" returned nothing,
 * "dark night" missed "The Dark Knight", "tom cruise mission" returned 0.
 *
 * This helper scores any candidate against a query so we can:
 *  - keep exact / prefix / "all words present" results at the top,
 *  - tolerate typos via Sørensen-Dice on character bigrams (cheap + good),
 *  - still defer to popularity inside each tier.
 *
 * The score is a single number; higher = better. Callers sort descending.
 */

const norm = (s: string) =>
  (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const bigrams = (s: string): Set<string> => {
  const out = new Set<string>();
  if (s.length < 2) {
    if (s) out.add(s);
    return out;
  }
  for (let i = 0; i < s.length - 1; i++) out.add(s.slice(i, i + 2));
  return out;
};

/** Sørensen-Dice coefficient (0–1) on character bigrams. */
const dice = (a: string, b: string): number => {
  const A = bigrams(a);
  const B = bigrams(b);
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  for (const g of A) if (B.has(g)) inter++;
  return (2 * inter) / (A.size + B.size);
};

/**
 * Score a candidate title against a query.
 *
 * Tier layout (added together with `popularity` mixed in as the tiebreaker):
 *   1000  exact title match
 *    800  title starts with the full query
 *    600  every query word appears in the title (any order)
 *    400  fuzzy: dice(title, query) >= 0.6
 *    200  fuzzy: dice(title, query) >= 0.45
 *      0  otherwise
 *
 * Plus a small popularity bonus (0–50) so within a tier the more
 * popular item wins.
 */
export interface RankableCandidate {
  title?: string | null;
  name?: string | null;
  popularity?: number;
}

export function scoreCandidate(item: RankableCandidate, query: string): number {
  const q = norm(query);
  if (!q) return 0;
  const title = norm(item.title || item.name || '');
  if (!title) return 0;

  let score = 0;

  if (title === q) {
    score = 1000;
  } else if (title.startsWith(q)) {
    score = 800;
  } else {
    const words = q.split(' ').filter(Boolean);
    if (words.length > 1 && words.every((w) => title.includes(w))) {
      score = 600;
    } else {
      const d = dice(title, q);
      if (d >= 0.6) score = 400;
      else if (d >= 0.45) score = 200;
      else if (title.includes(q)) score = 300; // substring fallback (TMDB-style)
    }
  }

  if (score === 0) return 0;

  // Popularity tiebreaker (TMDB popularity can be in the thousands; clamp).
  const pop = Math.min(item.popularity ?? 0, 1000) / 20; // 0–50
  return score + pop;
}

/** Sort candidates in-place by descending score; drop zero-score items. */
export function rankCandidates<T extends RankableCandidate>(items: T[], query: string): T[] {
  return items
    .map((item) => ({ item, score: scoreCandidate(item, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.item);
}
